// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import { getDocument } from '@atlaskit/browser-apis';

import { useFirePostOfficeTrigger } from '@atlassian/post-office-context/post-office-trigger';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useCallback, useEffect, useState } from 'react';

import { HighImpactModal } from './HighImpactModal';

import { HighImpactSpotlightTour } from './HighImpactSpotlightTour';

import {
	COPY_LIST_SETTINGS_NO,
	COPY_LIST_SETTINGS_MIGRATE_ALL_SPACES,
	COPY_LIST_SETTINGS_MIGRATE_CURRENT_SPACE,
} from '../placements/in-app-v2/issue-navigator-placement/constants';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { type JSX } from 'react/jsx-runtime';

// These triggers correspond to the Listener in the issue navigator package
// jira/src/packages/issue-navigator/issue-navigator/src/ui/spotlight/migrate-list-config-click-listener/MigrateListConfigClickListener.tsx
const MIGRATE_CONFIG_TRIGGER_PAID_CURRENT_SPACE =
	'jira-nin-paid-list-merge-modal-onboarding-explore-list-view-button-clicked-migrate-current-space';

const MIGRATE_CONFIG_TRIGGER_PAID_ALL_SPACES =
	'jira-nin-paid-list-merge-modal-onboarding-explore-list-view-button-clicked-migrate-all-spaces';

type OnboardingStep = 'modal' | 'spotlight-tour' | 'dismissed';

type HighImpactOnboardingProps = {
	hasListPreference?: boolean;
	projectName?: string;
};

export const HighImpactOnboarding = ({
	hasListPreference,
	projectName,
}: HighImpactOnboardingProps): JSX.Element | null => {
	const [currentStep, setCurrentStep] = useState<OnboardingStep>('modal');
	const [copyListSettings, setCopyListSettings] = useState<string | null>(null);
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { onAcknowledged, onClosed } = usePostOfficeUserInteractions();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	const dismissMessage = useCallback(
		(isTourCompleted = false) => {
			setCurrentStep('dismissed');
			if (isTourCompleted) {
				void onAcknowledged({ interactionIdentifier: 'tourCompleted' });
			} else {
				void onClosed({ interactionIdentifier: 'dismiss' });
			}
			fireAnalyticsEvent({
				eventType: 'track',
				action: 'completed',
				actionSubject: 'jiraNinPaidListMergeTourCompleted',
				attributes: {
					didCompleteTour: isTourCompleted,
				},
			});
		},
		[onAcknowledged, onClosed, fireAnalyticsEvent],
	);

	const fireExploreEvent = useCallback(() => {
		let clickIdentifier = 'exploreFilters';
		let settingIdentifier = 'WithCopyListSettings';
		if (copyListSettings === COPY_LIST_SETTINGS_MIGRATE_ALL_SPACES) {
			settingIdentifier = 'WithCopyListSettingsAllSpaces';
		} else if (copyListSettings === COPY_LIST_SETTINGS_NO) {
			settingIdentifier = 'WithoutCopyListSettings';
		}

		if (hasListPreference) clickIdentifier += settingIdentifier;

		void messageInstanceClicked({
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {},
			clickedElement: {
				buttonType: 'secondary',
				clickedElement: 'button',
				clickIdentifier,
			},
			messageEventType: 'clicked',
		});
	}, [messageInstanceClicked, copyListSettings]);

	useEffect(() => {
		const handleEscape = (ev: KeyboardEvent) => {
			if (ev.key === 'Escape') {
				dismissMessage();
			}
		};

		getDocument()?.addEventListener('keydown', handleEscape);

		return () => {
			getDocument()?.removeEventListener('keydown', handleEscape);
		};
	}, [dismissMessage]);
	const { fireTrigger: fireExploreListViewButtonTriggerMigrateCurrentSpace } =
		useFirePostOfficeTrigger({
			triggerId: MIGRATE_CONFIG_TRIGGER_PAID_CURRENT_SPACE,
		});
	const { fireTrigger: fireExploreListViewButtonTriggerMigrateAllSpaces } =
		useFirePostOfficeTrigger({
			triggerId: MIGRATE_CONFIG_TRIGGER_PAID_ALL_SPACES,
		});

	const handleExploreListViewButtonClick = useCallback(() => {
		switch (copyListSettings) {
			case COPY_LIST_SETTINGS_MIGRATE_CURRENT_SPACE:
				fireExploreListViewButtonTriggerMigrateCurrentSpace({ isActive: true });
				break;
			case COPY_LIST_SETTINGS_MIGRATE_ALL_SPACES:
				fireExploreListViewButtonTriggerMigrateAllSpaces({ isActive: true });
				break;
			default:
				break;
		}
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'jiraNinPaidListMergeExploreListViewButton',
			attributes: {
				copyListSettings,
			},
		});

		setCurrentStep('spotlight-tour');
		fireExploreEvent();
	}, [
		copyListSettings,
		fireExploreListViewButtonTriggerMigrateCurrentSpace,
		fireExploreListViewButtonTriggerMigrateAllSpaces,
		fireExploreEvent,
	]);

	switch (currentStep) {
		case 'modal':
			return (
				<HighImpactModal
					hasListPreference={hasListPreference}
					onClose={() => dismissMessage()}
					onExploreListViewButtonClick={handleExploreListViewButtonClick}
					copyListSettings={copyListSettings}
					setCopyListSettings={setCopyListSettings}
					projectName={projectName}
				/>
			);
		case 'spotlight-tour':
			return <HighImpactSpotlightTour onDismiss={dismissMessage} />;
		default:
			return null;
	}
};
